import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CardStack, { type StackCard } from '../components/CardStack';
import { showConfirm } from './ConfirmDialog';
import type {
  AdvancingSettings,
  AdvancingShare,
  AdvancingShareScope,
  AdvancingHistoryEntry,
  AdvancingProgress,
  ShowDTO,
} from '../api';
import {
  apiGetShow,
  apiGetAdvancingSettings,
  apiUpdateAdvancingSettings,
  apiGetAdvancingShares,
  apiCreateAdvancingShare,
  apiDeleteAdvancingShare,
  apiGetAdvancingHistory,
  apiPostAdvancingHistory,
  apiGetAdvancingProgress,
  apiGetPromoToken,
} from '../api';
import { buildAdvancingEmail } from '../lib/advancingEmail';
import { useWriteGate } from '../lib/writeGate';
import PdfPreview from './PdfPreview';

/**
 * AdvancingTab — the "Advancing" tab of the Show detail page (manager-side hub).
 *
 * Phase B of Advancing-Spec.md. Centralises everything asked of the promoter for
 * this show (tech + hotel + logistics), exposed to the organiser via one share
 * link per category. The full public per-token pages the links point to are
 * Phase C — here we just create / copy / delete the links and keep the flags and
 * exchange history.
 *
 * Self-contained: it loads its own settings / shares / history on mount and
 * mutates them directly, so it only needs the show id (+ band id, currently
 * unused beyond the prop contract, kept for the Phase C deep links).
 */
export interface AdvancingTabProps {
  showId: string | null;
  bandId: string;
  /** Open the show's Tech tab (the rider lives there) — shown as a header
      action so the manager can jump from Advancing to the show's rider. */
  onOpenRider?: () => void;
  /** Build the show rider docDef (same builder as the Tech tab preview) —
      powers the rider preview card at the TOP of the tab (2026-10-23). */
  riderPdfLoader?: () => Promise<any | null>;
}

const SCOPES: { scope: AdvancingShareScope; label: string; emoji: string; flag: 'tech_enabled' | 'hotel_enabled' | 'logistics_enabled' }[] = [
  { scope: 'tech', label: 'advancing.scopeTechnical', emoji: '🔧', flag: 'tech_enabled' },
  { scope: 'hotel', label: 'advancing.scopeHotel', emoji: '🏨', flag: 'hotel_enabled' },
  { scope: 'logistics', label: 'advancing.scopeLogistics', emoji: '🚚', flag: 'logistics_enabled' },
  { scope: 'all', label: 'advancing.scopeAll', emoji: '📋', flag: 'tech_enabled' },
];

const SCOPE_LABEL: Record<AdvancingShareScope, string> = {
  tech: 'advancing.scopeTechnical',
  hotel: 'advancing.scopeHotel',
  logistics: 'advancing.scopeLogistics',
  all: 'advancing.scopeAll',
};

export default function AdvancingTab({ showId, bandId, onOpenRider, riderPdfLoader }: AdvancingTabProps) {
  const { t } = useTranslation();
  // Capability-aware write gate (lot A1): a global read-only account (even with a
  // band editor role) must not emit these writes — the server 403s them and the
  // user would read a « Not saved » banner on an impossible action.
  const gate = useWriteGate(bandId);
  const canWrite = gate.canWrite;
  const [settings, setSettings] = useState<AdvancingSettings | null>(null);
  const [shares, setShares] = useState<AdvancingShare[]>([]);
  const [history, setHistory] = useState<AdvancingHistoryEntry[]>([]);
  // Phase E — advancement dashboard (per scope + global roll-up).
  const [progress, setProgress] = useState<AdvancingProgress | null>(null);
  // The show row (loaded for the advancing email generator). Optional: the
  // email degrades gracefully if it fails to load.
  const [show, setShow] = useState<ShowDTO | null>(null);
  // Public Promo kit link for this band — included in the promoter email when
  // the caller can see the token (editor+). A viewer's fetch 403s and the email
  // simply omits the Promo kit section.
  const [promoKitUrl, setPromoKitUrl] = useState<string | null>(null);
  const [emailText, setEmailText] = useState<string | null>(null);
  const [emailCopied, setEmailCopied] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Deadline inputs — one text input per category, held locally so we don't
  // re-render the whole settings blob on every keystroke.
  const [deadlines, setDeadlines] = useState<Record<string, string>>({});
  const [webhookUrl, setWebhookUrl] = useState('');
  // New-history-entry form state.
  const [newAuthor, setNewAuthor] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newScope, setNewScope] = useState<AdvancingShareScope>('all');

  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Rider preview at the TOP of the tab (user 2026-10-23): link to the Tech
  // tab + the SAME rider PDF as the Tech tab preview, built on mount (and
  // whenever the loader identity changes — i.e. the rider/selection changed).
  // Built lazily and only when the loader exists; a failure stays silent (the
  // Tech tab carries the full editor + diagnostics).
  const [riderDocDef, setRiderDocDef] = useState<any>(null);
  const [riderDocLoading, setRiderDocLoading] = useState(false);
  useEffect(() => {
    if (!riderPdfLoader) { setRiderDocDef(null); return; }
    let cancelled = false;
    setRiderDocLoading(true);
    riderPdfLoader()
      .then((doc) => { if (!cancelled) setRiderDocDef(doc); })
      .catch(() => { if (!cancelled) setRiderDocDef(null); })
      .finally(() => { if (!cancelled) setRiderDocLoading(false); });
    return () => { cancelled = true; };
  }, [riderPdfLoader]);

  const load = useCallback(async () => {
    if (!showId) return;
    setLoading(true);
    setError(null);
    try {
      const [s, sh, h, showData, prog] = await Promise.all([
        apiGetAdvancingSettings(showId),
        apiGetAdvancingShares(showId),
        apiGetAdvancingHistory(showId),
        apiGetShow(showId),
        apiGetAdvancingProgress(showId),
      ]);
      setSettings(s);
      setDeadlines(s.deadlines || {});
      setWebhookUrl(s.webhook_url || '');
      setShares(sh);
      setHistory(h);
      setShow(showData);
      setProgress(prog);
    } catch (e: any) {
      setError(t('advancing.loadFailed', { msg: e.message || 'unknown error' }));
    } finally {
      setLoading(false);
    }
    // Promo kit link for the email — separate (best-effort): a viewer 403s and
    // the email just omits the Promo kit section.
    if (bandId) {
      try {
        const r = await apiGetPromoToken(bandId);
        setPromoKitUrl(`${window.location.origin}/share/promo/${r.token}`);
      } catch {
        setPromoKitUrl(null);
      }
    }
  }, [showId, bandId]);

  useEffect(() => {
    void load();
  }, [load]);

  // ---- Settings ----------------------------------------------------------
  const setFlag = useCallback(
    async (flag: 'hotel_booked_by_band' | 'tech_enabled' | 'hotel_enabled' | 'logistics_enabled', value: boolean) => {
      if (!showId || !settings || !canWrite) return;
      setSaving(true);
      setError(null);
      try {
        const updated = await apiUpdateAdvancingSettings(showId, { [flag]: value ? 1 : 0 });
        setSettings(updated);
      } catch (e: any) {
        setError(t('advancing.saveFailed', { msg: e.message || 'unknown error' }));
      } finally {
        setSaving(false);
      }
    },
    [showId, settings],
  );

  const saveDeadlines = useCallback(async () => {
    if (!showId || !settings || !canWrite) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiUpdateAdvancingSettings(showId, { deadlines });
      setSettings(updated);
    } catch (e: any) {
      setError(t('advancing.saveFailed', { msg: e.message || 'unknown error' }));
    } finally {
      setSaving(false);
    }
  }, [showId, settings, deadlines]);

  const saveWebhook = useCallback(async () => {
    if (!showId || !settings || !canWrite) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiUpdateAdvancingSettings(showId, { webhook_url: webhookUrl.trim() });
      setSettings(updated);
      setWebhookUrl(updated.webhook_url || '');
    } catch (e: any) {
      setError(t('advancing.saveFailed', { msg: e.message || 'unknown error' }));
    } finally {
      setSaving(false);
    }
  }, [showId, settings, webhookUrl]);

  // ---- Shares ------------------------------------------------------------
  const createShare = useCallback(
    async (scope: AdvancingShareScope) => {
      if (!showId || !canWrite) return;
      setError(null);
      try {
        const created = await apiCreateAdvancingShare(showId, { scope });
        setShares((prev) => {
          const others = prev.filter((x) => x.scope !== scope);
          return [...others, created];
        });
      } catch (e: any) {
        setError(t('advancing.createFailed', { msg: e.message || 'unknown error' }));
      }
    },
    [showId],
  );

  const deleteShare = useCallback(
    async (share: AdvancingShare) => {
      if (!showId || !canWrite) return;
      const ok = await showConfirm(
        `Delete the "${SCOPE_LABEL[share.scope]}" advancing link? The promoter will no longer be able to reach it.`,
        { title: 'Delete link', confirmLabel: 'Delete', danger: true },
      );
      if (!ok) return;
      setError(null);
      try {
        await apiDeleteAdvancingShare(showId, share.id);
        setShares((prev) => prev.filter((x) => x.id !== share.id));
      } catch (e: any) {
        setError(t('advancing.deleteFailed', { msg: e.message || 'unknown error' }));
      }
    },
    [showId],
  );

  const copyShare = useCallback(async (token: string) => {
    const url = `${window.location.origin}/share/advancing/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 1500);
    } catch {
      // clipboard unavailable — leave the URL visible in the field to copy manually
    }
  }, []);

  // ---- Advancing email generator (Phase D) ----
  const generateEmail = useCallback(() => {
    const text = buildAdvancingEmail(
      show ?? {},
      show ? { name: show.venue_name, city: show.city } : null,
      show ? { name: show.band_name, promoKitUrl: promoKitUrl ?? undefined } : null,
      shares,
      settings,
      window.location.origin,
    );
    setEmailText(text);
    setEmailCopied(false);
  }, [show, shares, settings, promoKitUrl]);

  const copyEmail = useCallback(async () => {
    if (emailText == null) return;
    try {
      await navigator.clipboard.writeText(emailText);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 1500);
    } catch {
      // clipboard unavailable — the text stays selectable in the textarea
    }
  }, [emailText]);

  // ---- History -----------------------------------------------------------
  const addHistoryEntry = useCallback(async () => {
    if (!showId || !canWrite) return;
    if (!newMessage.trim() && !newAuthor.trim()) return;
    setError(null);
    try {
      const entry = await apiPostAdvancingHistory(showId, {
        author: newAuthor.trim(),
        message: newMessage.trim(),
        scope: newScope,
      });
      setHistory((prev) => [...prev, entry]);
      setNewMessage('');
      setNewAuthor('');
    } catch (e: any) {
      setError(t('advancing.addEntryFailed', { msg: e.message || 'unknown error' }));
    }
  }, [showId, newMessage, newAuthor, newScope]);

  // Whether a scope's link should be greyed out because its category is off.
  const scopeEnabled = (scope: AdvancingShareScope): boolean => {
    if (!settings) return true;
    if (scope === 'all') {
      return !!(settings.tech_enabled || settings.hotel_enabled || settings.logistics_enabled);
    }
    if (scope === 'hotel') {
      // No hotel request when the band books it itself OR the category is off.
      return !!settings.hotel_enabled && !settings.hotel_booked_by_band;
    }
    const flag = SCOPES.find((s) => s.scope === scope)?.flag;
    return flag ? !!settings[flag] : true;
  };

  if (loading) {
    return (
      <div className="card" data-advancing="loading" style={{ maxWidth: 1000, marginBottom: 20 }}>
        <p className="text-dim" style={{ color: 'var(--text-dim)' }}>{t('advancing.loading')}</p>
      </div>
    );
  }

  const TAB_CARDS: StackCard[] = [
    // LOT E1 (2026-11-02): `hasData` = the card has real content to show.
    // `defaultOpen` is gone: it used to open ALL seven cards, including the
    // empty ones (settings / email are tools, not data).
    { id: 'rider', hasData: !!riderDocDef, title: t('advancing.riderTitle'), sub: t('advancing.riderHint'), node: onOpenRider ? (
        <div data-advancing-rider-card>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <button type="button" className="btn btn-secondary btn-small" onClick={onOpenRider}>
              {t('advancing.viewRider')}
            </button>
          </div>
          {riderDocLoading && <p style={{ color: 'var(--text-dim)' }}>{t('advancing.riderLoading')}</p>}
          {!riderDocLoading && riderDocDef && (
            <PdfPreview docDef={riderDocDef} height={420} />
          )}
        </div>) : null },
    { id: 'progress', hasData: !!progress && progress.global.total > 0, title: t('advancing.progressTitle'), sub: t('advancing.progressHint'), node: (<div data-advancing="progress">
  {progress ? (
    <>
      <div className="rc-list" data-advancing-bars>
        {(['tech', 'hotel', 'logistics'] as const).map((scope) => {
          const s = progress.scopes[scope];
          const label = SCOPE_LABEL[scope];
          const pct = s.total > 0 ? Math.round((s.confirmed / s.total) * 100) : 0;
          const done = s.status === 'complete' && s.total > 0;
          return (
            <div key={scope} className="rc-card col">
              <div className="rc-card-fields" style={{ justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600 }}>
                  {t(label)} <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>{t('advancing.confirmed', { confirmed: s.confirmed, total: s.total })}</span>
                  {done && <span title={t('advancing.fullyConfirmed')}> ✅</span>}
                </span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                  {s.remarked > 0 ? t('advancing.withRemarks', { count: s.remarked }) : ''}
                </span>
              </div>
              <div
                style={{
                  marginTop: 6,
                  height: 10,
                  borderRadius: 5,
                  background: 'color-mix(in srgb, var(--text-muted) 15%, transparent)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${pct}%`,
                    borderRadius: 5,
                    background: done ? 'var(--success)' : 'var(--accent)',
                    transition: 'width .3s ease',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div
        className="rc-card-fields"
        data-advancing-total
        style={{
          marginTop: 18,
          paddingTop: 14,
          borderTop: '1px solid var(--border)',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontWeight: 700 }}>
          {t('advancing.totalConfirmed', { confirmed: progress.global.confirmed, total: progress.global.total })}
          {progress.global.status === 'complete' && progress.global.total > 0 && <span title={t('advancing.fullyConfirmed')}> ✅</span>}
        </span>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>
          {t('advancing.stillToConfirm', { count: Math.max(0, progress.global.total - progress.global.confirmed) })}
        </span>
      </div>
    </>
  ) : (
    <p style={{ color: 'var(--text-dim)', margin: 0 }}>{t('advancing.noProgress')}</p>
  )}
</div>) },
    { id: 'email', hasData: false, title: t('advancing.emailTitle'), sub: t('advancing.emailHint'), node: (<div data-advancing="email">
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
    <button className="btn btn-primary" onClick={generateEmail}>
      {t('advancing.generateEmail')}
    </button>
    {emailText != null && (
      <button className="btn btn-small" onClick={() => void copyEmail()}>
        {emailCopied ? t('advancing.copied') : t('advancing.copy')}
      </button>
    )}
  </div>
  {emailText != null && (
    <textarea
      className="form-input"
      readOnly
      rows={18}
      value={emailText}
      style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}
      onFocus={(e) => e.target.select()}
    />
  )}
</div>) },
    { id: 'overview', hasData: shares.length > 0, title: t('advancing.overviewTitle'), sub: t('advancing.overviewHint'), node: (<div data-advancing="overview">
  <div className="rc-list" data-advancing-scopes>
    {SCOPES.map(({ scope, label, emoji }) => {
      const share = shares.find((x) => x.scope === scope);
      const enabled = scopeEnabled(scope);
      return (
        <div
          key={scope}
          className="rc-card"
          data-advancing-scope={scope}
          style={{ opacity: enabled ? 1 : 0.5 }}
        >
          <span className="rc-card-ic">{emoji}</span>
          <span className="rc-card-body">
            <span className="rc-card-name">{t(label)}</span>
            <span className="rc-card-meta">
              <span>
                {share ? t('advancing.linkReady') : t('advancing.noLinkYet')}
                {!enabled && t('advancing.disabled')}
              </span>
            </span>
          </span>
        </div>
      );
    })}
  </div>
</div>) },
    { id: 'settings', hasData: false, title: t('advancing.settingsTitle'), sub: t('advancing.settingsHint'), node: (<div data-advancing="settings">

  <div data-advancing-flags>
    <label className="rc-form-row" data-advancing-flag="hotel_booked_by_band" style={{ fontSize: '0.95rem' }}>
      <input
        type="checkbox"
        checked={!!settings?.hotel_booked_by_band}
        onChange={(e) => void setFlag('hotel_booked_by_band', e.target.checked)}
        disabled={saving || !canWrite}
      />
      {t('advancing.hotelBookedByBand')}
      <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)' }}>
        {t('advancing.hotelBookedHint')}
      </span>
    </label>
    <label className="rc-form-row" data-advancing-flag="tech_enabled" style={{ fontSize: '0.95rem' }}>
      <input
        type="checkbox"
        checked={!!settings?.tech_enabled}
        onChange={(e) => void setFlag('tech_enabled', e.target.checked)}
        disabled={saving || !canWrite}
      />
      {t('advancing.techEnabled')}
    </label>
    <label className="rc-form-row" data-advancing-flag="hotel_enabled" style={{ fontSize: '0.95rem' }}>
      <input
        type="checkbox"
        checked={!!settings?.hotel_enabled}
        onChange={(e) => void setFlag('hotel_enabled', e.target.checked)}
        disabled={saving || !canWrite}
      />
      {t('advancing.hotelEnabled')}
    </label>
    <label className="rc-form-row" data-advancing-flag="logistics_enabled" style={{ fontSize: '0.95rem' }}>
      <input
        type="checkbox"
        checked={!!settings?.logistics_enabled}
        onChange={(e) => void setFlag('logistics_enabled', e.target.checked)}
        disabled={saving || !canWrite}
      />
      {t('advancing.logisticsEnabled')}
    </label>
  </div>

  <h4 className="rc-section-title" style={{ margin: '16px 0 10px' }}>{t('advancing.deadlinesTitle')}</h4>
  {/* Échéances EMPILÉES (U2) : trois colonnes de 150px donnaient trois axes
      (ancres 558 / 847 / …) — chaque échéance suit maintenant la grille de la
      page, une par ligne. */}
  <div data-advancing-deadlines style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 8 }}>
    {(['tech', 'hotel', 'logistics'] as const).map((cat) => (
      <div key={cat} className="form-field" style={{ minWidth: 0 }}>
        <label>{t(SCOPE_LABEL[cat])}</label>
        <input
          className="form-input"
          type="date"
          value={deadlines[cat] || ''}
          onChange={(e) => setDeadlines((d) => ({ ...d, [cat]: e.target.value }))}
          disabled={!canWrite}
        />
      </div>
    ))}
    <button className="btn btn-small btn-primary" onClick={() => void saveDeadlines()} disabled={saving || !canWrite} style={{ justifySelf: 'start' }}>
      {t('advancing.saveDeadlines')}
    </button>
  </div>

  <h4 className="rc-section-title" style={{ margin: '16px 0 10px' }}>{t('advancing.webhookTitle')}</h4>
  <div className="rc-card-fields" data-advancing-webhook>
    <input
      className="form-input"
      type="url"
      placeholder={t('advancing.webhookPh')}
      style={{ flex: '1 1 360px', minWidth: 0 }}
      value={webhookUrl}
      onChange={(e) => setWebhookUrl(e.target.value)}
      disabled={saving || !canWrite}
    />
    <button className="btn btn-small btn-primary" onClick={() => void saveWebhook()} disabled={saving || !canWrite}>
      {t('advancing.saveWebhook')}
    </button>
  </div>
  <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)', marginBottom: 0 }}>
    {t('advancing.webhookHint')}
  </p>
</div>) },
    { id: 'shares', hasData: shares.length > 0, title: t('advancing.shareLinksTitle'), sub: t('advancing.shareLinksHint'), node: (<div data-advancing="shares">
  <div className="rc-list" data-advancing-share-list>
    {SCOPES.map(({ scope, label, emoji }) => {
      const share = shares.find((x) => x.scope === scope);
      const enabled = scopeEnabled(scope);
      const url = share ? `${window.location.origin}/share/advancing/${share.token}` : '';
      return (
        <div
          key={scope}
          className="rc-card col"
          data-advancing-share={scope}
          style={{ opacity: enabled ? 1 : 0.45 }}
        >
          <div className="rc-card-fields">
            <span className="rc-card-name" style={{ flex: '0 0 auto', minWidth: 140 }}>{emoji} {t(label)}</span>
            {share ? (
              <>
                <input
                  className="form-input"
                  readOnly
                  style={{ flex: '1 1 320px', minWidth: 0 }}
                  value={url}
                  onFocus={(e) => e.target.select()}
                />
                <button className="btn btn-small" onClick={() => void copyShare(share.token)}>
                  {copiedToken === share.token ? t('advancing.copied') : t('advancing.copy')}
                </button>
                <button className="btn btn-small btn-danger" onClick={() => void deleteShare(share)} disabled={!canWrite}>
                  {t('advancing.deleteLink')}
                </button>
              </>
            ) : (
              <button
                className="btn btn-small btn-primary"
                onClick={() => void createShare(scope)}
                disabled={!enabled || !canWrite}
              >
                {t('advancing.createLink')}
              </button>
            )}
          </div>
          {!enabled && (
            <div className="text-dim" style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)' }}>
              {scope === 'hotel' && settings?.hotel_booked_by_band
                ? t('advancing.hotelNoLink')
                : t('advancing.categoryDisabled')}
            </div>
          )}
        </div>
      );
    })}
  </div>
</div>) },
    { id: 'history', hasData: history.length > 0, title: `💬 ${t('advancing.historyTitle')} (${history.length})`, sub: t('advancing.historyHint'), node: (<div data-advancing="history">

  <div className="rc-card-fields" data-advancing-new-entry style={{ marginBottom: 12, alignItems: 'flex-end' }}>
    <input
      className="form-input"
      style={{ flex: '0 1 160px', minWidth: 140 }}
      placeholder={t('advancing.authorPh')}
      value={newAuthor}
      onChange={(e) => setNewAuthor(e.target.value)}
      disabled={!canWrite}
    />
    <select
      className="form-input"
      style={{ flex: '0 1 150px', minWidth: 130 }}
      value={newScope}
      onChange={(e) => setNewScope(e.target.value as AdvancingShareScope)}
      disabled={!canWrite}
    >
      {SCOPES.map(({ scope, label }) => (
        <option key={scope} value={scope}>{label}</option>
      ))}
    </select>
    <input
      className="form-input"
      style={{ flex: '1 1 240px', minWidth: 180 }}
      placeholder={t('advancing.messagePh')}
      value={newMessage}
      onChange={(e) => setNewMessage(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') void addHistoryEntry(); }}
      disabled={!canWrite}
    />
    <button
      className="btn btn-primary"
      onClick={() => void addHistoryEntry()}
      disabled={(!newMessage.trim() && !newAuthor.trim()) || !canWrite}
    >
      {t('advancing.addEntry')}
    </button>
  </div>

  {history.length === 0 ? (
    <p className="text-dim" style={{ color: 'var(--text-dim)' }}>{t('advancing.noExchanges')}</p>
  ) : (
    <div className="rc-list" data-advancing-history-list>
      {history.map((h) => (
        <div key={h.id} className="rc-card col" data-advancing-entry>
          <div className="rc-card-meta" style={{ marginTop: 0 }}>
            <span><strong>{h.author || '—'}</strong></span>
            <span>{t(SCOPE_LABEL[(h.scope as AdvancingShareScope) || 'all'] || h.scope || '—')}</span>
            {h.created_at ? <span>{h.created_at.slice(0, 10)}</span> : null}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{h.message}</div>
        </div>
      ))}
    </div>
  )}
</div>) },
  ];

  return (
<div data-advancing-tab>
      <CardStack pageId="show_advancing" cards={TAB_CARDS} foldable />
{error && (
        <div className="card" data-advancing="error" style={{ maxWidth: 1000, marginBottom: 20, borderColor: 'var(--danger)' }}>
          <p style={{ color: 'var(--danger)', margin: 0 }}>{error}</p>
        </div>
      )}
    </div>
  );
}
